import { forwardRef, ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { SimpleTooltip } from "@/components/flowly/infra/SimpleTooltip/SimpleTooltip";

const iconButtonVariants = cva(
  "rounded-full flex items-center justify-center transition-colors",
  {
    variants: {
      variant: {
        default: "bg-secondary hover:bg-muted text-muted-foreground hover:text-foreground",
        ghost: "text-muted-foreground hover:text-foreground hover:bg-secondary/50",
        overlay: "bg-black/50 hover:bg-black/70 text-white",
      },
      size: {
        sm: "w-8 h-8",
        md: "w-10 h-10",
        lg: "w-12 h-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
);

interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonVariants> {
  tooltip?: string;
  tooltipSide?: "top" | "bottom" | "left" | "right";
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant, size, tooltip, tooltipSide, children, ...props }, ref) => {
    const button = (
      <button
        ref={ref}
        className={cn(iconButtonVariants({ variant, size }), className)}
        {...props}
      >
        {children}
      </button>
    );

    if (!tooltip) return button;

    return (
      <SimpleTooltip content={tooltip} side={tooltipSide}>
        {button}
      </SimpleTooltip>
    );
  }
);

IconButton.displayName = "IconButton";

export { IconButton, iconButtonVariants };
